import { detectedTimezone } from '../utils/readerEmail';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BetaReaderShell } from '../components/BetaReaderShell';
import { ReaderHomeButton } from '../components/ReaderHomeButton';
import { getReaderEmail, requestEmailVerification, setReaderEmail, type ReaderEmail } from '../repositories/readerRepository';
import { useUser } from '../providers/UserProvider';

const HOUR_OPTIONS = [
  { value: 0, label: '12:00 AM' },
  { value: 1, label: '1:00 AM' },
  { value: 2, label: '2:00 AM' },
  { value: 3, label: '3:00 AM' },
  { value: 4, label: '4:00 AM' },
  { value: 5, label: '5:00 AM' },
  { value: 6, label: '6:00 AM' },
  { value: 7, label: '7:00 AM' },
  { value: 8, label: '8:00 AM' },
  { value: 9, label: '9:00 AM' },
  { value: 10, label: '10:00 AM' },
  { value: 11, label: '11:00 AM' },
  { value: 12, label: '12:00 PM' },
  { value: 13, label: '1:00 PM' },
  { value: 14, label: '2:00 PM' },
  { value: 15, label: '3:00 PM' },
  { value: 16, label: '4:00 PM' },
  { value: 17, label: '5:00 PM' },
  { value: 18, label: '6:00 PM' },
  { value: 19, label: '7:00 PM' },
  { value: 20, label: '8:00 PM' },
  { value: 21, label: '9:00 PM' },
  { value: 22, label: '10:00 PM' },
  { value: 23, label: '11:00 PM' },
];

// The plan's default: 6am in the reader's own time zone, ahead of the newsletters.
const DEFAULT_SEND_HOUR = 6;

const TIMEZONE_OPTIONS = [
  { value: 'America/Los_Angeles', label: 'Pacific Time (US & Canada)' },
  { value: 'America/Denver', label: 'Mountain Time (US & Canada)' },
  { value: 'America/Chicago', label: 'Central Time (US & Canada)' },
  { value: 'America/New_York', label: 'Eastern Time (US & Canada)' },
  { value: 'America/Anchorage', label: 'Alaska Time (US)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (US)' },
  { value: 'America/Halifax', label: 'Atlantic Time (Canada)' },
  { value: 'America/Sao_Paulo', label: 'Brasília Time (Brazil)' },
  { value: 'America/Buenos_Aires', label: 'Buenos Aires (Argentina)' },
  { value: 'Europe/London', label: 'London (GMT / BST)' },
  { value: 'Europe/Paris', label: 'Central European Time (Paris, Berlin, Rome)' },
  { value: 'Europe/Helsinki', label: 'Eastern European Time (Helsinki, Athens)' },
  { value: 'Asia/Jerusalem', label: 'Jerusalem (IST)' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  { value: 'Asia/Kolkata', label: 'India Standard Time (IST)' },
  { value: 'Asia/Bangkok', label: 'Indochina Time (Bangkok, Jakarta)' },
  { value: 'Asia/Singapore', label: 'Singapore, Hong Kong, Beijing (SGT)' },
  { value: 'Asia/Tokyo', label: 'Japan & Korea (JST/KST)' },
  { value: 'Australia/Sydney', label: 'Australian Eastern Time (Sydney, Melbourne)' },
  { value: 'Pacific/Auckland', label: 'New Zealand Time (Auckland)' },
  { value: 'UTC', label: 'UTC' },
];

/**
 * Whether the form differs from what the server last reported.
 *
 * Only the fields the form can edit are compared. A missing send hour and an
 * empty time zone are read the way the form displays them, so a reader who
 * has never saved and sees the defaults is not offered a Save that would
 * change nothing.
 */
export function isEmailSettingsDirty(settings: ReaderEmail | null, initial: ReaderEmail | null): boolean {
  if (!settings || !initial) return false;
  return settings.active !== initial.active ||
    (settings.send_hour ?? DEFAULT_SEND_HOUR) !== (initial.send_hour ?? DEFAULT_SEND_HOUR) ||
    (settings.timezone || detectedTimezone()) !== (initial.timezone || detectedTimezone());
}

export default function ReaderEmailPage({
  initialSettings: propsInitialSettings = null,
}: {
  initialSettings?: ReaderEmail | null;
} = {}) {
  const { user, logout } = useUser();
  const [initialSettings, setInitialSettings] = useState<ReaderEmail | null>(propsInitialSettings);
  const [settings, setSettings] = useState<ReaderEmail | null>(propsInitialSettings);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [verificationSent, setVerificationSent] = useState(false);

  useEffect(() => {
    let disposed = false;
    getReaderEmail().then((email) => {
      if (disposed) return;
      // The plan detects the time zone rather than asking for it. A reader who
      // has never saved settings gets the zone their browser is in, already
      // selected, so 6am local means their local.
      const resolved = email.configured || email.timezone ? email : { ...email, timezone: detectedTimezone() };
      setInitialSettings(resolved);
      setSettings(resolved);
    }).catch(err => { if (!disposed) setError(err.message); });
    return () => { disposed = true; };
  }, [refresh]);

  const isDirty = isEmailSettingsDirty(settings, initialSettings);

  const save = async () => {
    if (!settings || !isDirty) return;
    setBusy(true); setSaved(false); setError('');
    try {
      const updated = await setReaderEmail(settings);
      setInitialSettings(updated);
      setSettings(updated);
      setSaved(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const sendVerification = async () => {
    setBusy(true); setError('');
    try {
      const result = await requestEmailVerification();
      if (result.email_verified) setRefresh(value => value + 1);
      else setVerificationSent(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // An address nobody has proven cannot be turned on (PRO-701); turning an
  // active one off always stays possible.
  const lockedOff = settings !== null && !settings.email_verified && !settings.active;

  return <BetaReaderShell>
    <div className="beta-search-options-bar" role="toolbar" aria-label="Page options"><ReaderHomeButton /></div>
    <h1 className="beta-settings-title">Email Settings</h1>
    <p className="beta-message">Your personalized TLDR, with stories from the same interests as your home feed.</p>
    {error && <p className="beta-error" role="alert">{error} <button onClick={() => { setError(''); setRefresh(value => value + 1); }}>Retry</button></p>}
    {lockedOff && <div className="beta-message" role="status">
      {verificationSent
        ? <p>We sent a link to {user?.email}. Open it to confirm your email address, then come back here to turn on email.</p>
        : <p>Confirm your email address before we send TLDR For You to {user?.email}.</p>}
      <button type="button" className="beta-pill" disabled={busy} onClick={() => void sendVerification()}>
        {verificationSent ? 'Send the link again' : 'Send confirmation link'}
      </button>
    </div>}
    {settings ? <form className="beta-email-form" onSubmit={event => { event.preventDefault(); void save(); }}>
      <label className="beta-email-enabled"><input type="checkbox" checked={settings.active} disabled={busy || lockedOff}
        onChange={event => { setSaved(false); setSettings({ ...settings, active: event.target.checked }); }} />Email me TLDR For You</label>
      <label>Delivery time<select value={settings.send_hour ?? DEFAULT_SEND_HOUR} disabled={busy || !settings.active}
        onChange={event => { setSaved(false); setSettings({ ...settings, send_hour: parseInt(event.target.value, 10) }); }}>
        {HOUR_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select></label>
      <label>Time zone<select value={settings.timezone || detectedTimezone()} disabled={busy || !settings.active}
        onChange={event => { setSaved(false); setSettings({ ...settings, timezone: event.target.value }); }}>
        {!TIMEZONE_OPTIONS.some(tz => tz.value === settings.timezone) && settings.timezone && (
          <option value={settings.timezone}>{settings.timezone}</option>
        )}
        {TIMEZONE_OPTIONS.map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
      </select></label>
      <p className="beta-message">Daily emails arrive at your chosen hour with up to {settings.article_count} stories. We skip an email when there are no new stories.</p>
      <button className="beta-pill selected" type="submit" disabled={busy || !isDirty}>{busy ? 'Saving…' : 'Save settings'}</button>
      {saved && <p role="status">{settings.active ? 'Your email settings are saved.' : 'You’re unsubscribed. Your feed is still here.'}</p>}
    </form> : !error && <p className="beta-message" role="status">Loading email settings…</p>}
    <div className="beta-settings-footer"><Link to="/?edit=1">Edit interests</Link><button type="button" onClick={logout}>Log out</button></div>
  </BetaReaderShell>;
}
